import cloneDeep from "lodash.clonedeep";
import { IMetricConflict } from "../sessions/models";
import { IGoal } from "@gtmhub/goals/models";
import { ICloningWorkerData, IPlanningSession, IPlanningSessionConflict } from "@gtmhub/sessions/models/models";
import { ICollection } from "@gtmhub/models";
import { ICheckin, IMetric } from "@gtmhub/okrs/metrics";
import { batchGenerator } from "@gtmhub/util";

export const strings = {
  dueDate: "dueDate",
  critical: "Critical",
  metricTypes: {
    hard: "hardDeadline",
    soft: "softDeadline",
    hardAndSoft: "hardAndSoft",
  },
};

interface ICheckinMetricHolder {
  checkin: ICheckin;
  metric: IMetric;
}

export function isDefaultMetricDueDate(date: string, metricDefaultDueDate = "0001-01-01T00:00:00Z"): boolean {
  return date === metricDefaultDueDate;
}

export function isOutOfSessionBounds(date: string, session: IPlanningSession): boolean {
  return date < session.start || date > session.end;
}

export function filterConflictMetrics(metrics: IMetricConflict[]): IMetricConflict[] {
  return metrics.filter((metric) => metric.type);
}

export function updateMetricsDueDates(goal: IGoal, session: IPlanningSession): IMetric[] {
  return (goal.metrics = cloneDeep(goal.metrics).map((metric: IMetric) => {
    // hard and soft deadline
    // if (metric.dueDate !== metricDefaultDueDate && metric.softDueDate !== metricDefaultDueDate) {
    if (
      !isDefaultMetricDueDate(metric.dueDate) &&
      !isDefaultMetricDueDate(metric.softDueDate) &&
      (isOutOfSessionBounds(metric.dueDate, session) || isOutOfSessionBounds(metric.softDueDate, session))
    ) {
      return { ...metric, type: strings.metricTypes.hardAndSoft };
    }

    // hard deadline
    if (!isDefaultMetricDueDate(metric.dueDate) && isOutOfSessionBounds(metric.dueDate, session)) {
      return { ...metric, type: strings.metricTypes.hard };
    }

    // soft deadline
    if (!isDefaultMetricDueDate(metric.softDueDate) && isOutOfSessionBounds(metric.softDueDate, session)) {
      return { ...metric, type: strings.metricTypes.soft };
    }

    return metric;
  }));
}

export const handleCollectionOperationsInBatches = async function <T, R>(
  collection: Array<T>,
  promiseMappingFn: (item: T, data: ICloningWorkerData) => Promise<R>,
  batchSize: number,
  data?: ICloningWorkerData
): Promise<R[]> {
  const clonedCollection: R[] = [];
  const collectionBatches = batchGenerator(collection, batchSize);

  let nextBatch = collectionBatches.next();

  do {
    const collectionBatch = nextBatch.value;
    if (Array.isArray(collectionBatch)) {
      const batchPromises = collectionBatch.map((item) => promiseMappingFn(item, data));

      const results = await Promise.allSettled(batchPromises);
      results.forEach((it) => {
        // Return all fulfilled promise results and ignore the rejected ones
        if (it.status === "fulfilled") {
          clonedCollection.push(it.value);
        }
      });
    }

    nextBatch = collectionBatches.next();
  } while (!nextBatch.done);

  return clonedCollection;
};

export function prepareCheckinData(metrics: IMetric[], currentUserId: string): ICheckinMetricHolder[] {
  const list: ICheckinMetricHolder[] = [];
  for (const metric of metrics) {
    if (metric.metricHistory && metric.metricHistory.length) {
      metric.metricHistory = metric.metricHistory.sort((a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime());

      for (const metricSnapshot of metric.metricHistory) {
        const checkin: ICheckin = {
          checkInDate: metricSnapshot.updatedAt,
          createdAt: metricSnapshot.createdAt,
          actual: metricSnapshot.metricValue,
          comment: metricSnapshot.comment,
          customFields: metric.customFields,
          confidence: metricSnapshot.confidenceValue,
        };

        if (metricSnapshot.changedBy.trim() != "") {
          checkin.changedBy = metricSnapshot.changedBy;
        } else {
          checkin.simulateAutomaticCheckin = true;
        }

        list.push({
          metric: metric,
          checkin: checkin,
        });
      }
    } else if (!metric.dueDate) {
      const checkin: ICheckin = {
        checkInDate: metric.lastCheckInDate,
        actual: metric.actual,
        changedBy: currentUserId,
        customFields: metric.customFields,
      };

      list.push({
        metric: metric,
        checkin: checkin,
      });
    }
  }

  return list;
}

class SharedWebWorkerService {
  public checkMetricConflicts(goals: IGoal[], session: IPlanningSession): ICollection<IPlanningSessionConflict> {
    const allMetricsConflicts = goals.reduce((aggregator, goal) => (goal.metrics ? aggregator.concat(updateMetricsDueDates(goal, session)) : aggregator), []); // checking for conflicts

    // returning in the data type the conflicts jade expects
    return {
      items: [
        {
          severity: strings.critical,
          type: strings.dueDate,
          goals: null,
          sessions: null,
          metrics: {
            items: filterConflictMetrics(allMetricsConflicts),
          },
        },
      ],
    };
  }
}

export const sharedWebWorkerService = new SharedWebWorkerService();
